import {
  conditionProducts,
  type OfferProduct,
  type ShippingCondition,
  type ShippingRate,
} from '@cod/config-schema';

/**
 * The Shipping Rates page's pure model.
 *
 * Separate from `ShippingRates.tsx` because that module pulls in Nimbus and the Nexo-backed api
 * client, which need a `window`. Keeping the rules here makes them testable in a node environment
 * — same split as `quantityOffers.model.ts`.
 */

/**
 * Whether the merchant may still delete a rate from this list.
 *
 * 🚨 The last rate cannot be deleted. Zero rates is not an invalid config — both renderers treat
 * it as "no rates configured" and fall back to the single static card built from
 * `shipping_method.option_label` (`form-tree/src/blocks.ts` `shippingNode`,
 * `form-renderer/src/form.ts`) — but it is not a state the merchant can *mean* to reach from this
 * page. Deleting down to nothing silently swaps every rate they priced for an unpriced
 * placeholder, and the page they'd have to fix it on is the one that just emptied itself.
 *
 * Takes the whole list rather than a count so callers cannot accidentally pass the SAVED length
 * while deleting from the STAGED one: deletes here are staged, so "how many are left" is only
 * true of the working list.
 */
export function canRemoveShippingRate(rates: readonly ShippingRate[]): boolean {
  return rates.length > 1;
}

/**
 * Remove a rate, or return the list **unchanged (by identity)** when that would empty it.
 *
 * The guard lives here and not only in the row's action cell: hiding the trash icon is what the
 * merchant sees, but any other path into deletion — a keyboard shortcut, a future bulk action,
 * a stale click on a row that another tab already reduced to one — reaches this function instead.
 * Callers can test `next === rates` to know nothing happened.
 */
export function removeShippingRate(rates: ShippingRate[], id: string): ShippingRate[] {
  if (!canRemoveShippingRate(rates)) return rates;
  const next = rates.filter((r) => r.id !== id);
  return next.length === rates.length ? rates : next;
}

/**
 * The picker's pre-checked list for a product condition.
 *
 * `OfferProduct` needs more than a condition stores (image, price, variants), so the extra fields
 * are filled with empties — the picker only keys on `id`, and `refreshSelected` replaces each
 * entry with the freshly fetched product as soon as the list loads.
 */
export function conditionPickerSelection(condition: ShippingCondition): OfferProduct[] {
  return conditionProducts(condition).map((p) => ({
    id: p.id,
    // `offerProduct.name` is `.min(1)`; a legacy condition can hold an empty name, and this list
    // is never persisted — it only tells the picker which rows to tick.
    name: p.name || String(p.id),
    variants: [],
    variant_options: [],
  }));
}

/**
 * What the picker's result becomes on the condition.
 *
 * 🚨 Clears the legacy `product_id` / `product_name` in the same patch. Leaving them behind would
 * give the condition two sources of truth, and `conditionProducts()` prefers the list — so a
 * merchant who narrowed a legacy condition to a different product would still ship the old id to
 * anything that read the legacy fields directly.
 */
export function conditionProductPatch(
  products: readonly OfferProduct[],
): Partial<ShippingCondition> {
  return {
    products: products.map((p) => ({ id: p.id, name: p.name })),
    product_id: undefined,
    product_name: undefined,
  };
}
